import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import merge from 'deepmerge';
import apiConfig from './apiConfig';
import { AuthProtect } from '@/common/enum';
import type EndPointTypes from 'interfaces/EndPointTypes.ts';
import { store } from '@/store';
import BaseURLConfig from '@/common/enum/BaseURLSelection.ts';

export const genericErrorMessage =
  'We just experienced a technical issue, please wait a few seconds and try again.';

// *: Fixed string with no interpolated request details, so a missing credential
// *: is diagnosable from the console without the token ever being logged.
export const missingJwtWarning =
  'apiFetch: authToken.jwtToken is empty, sending no Authorization header. ' +
  'AuthPage dispatches SET_JWT_TOKEN(idToken) on login; until it does, the ' +
  'damageassessment API rejects every authenticated request.';

const redactedValue = '[redacted]';

// *: Transport-level backstop so a hung backend fails the request instead of
// *: leaving the caller on a spinner until the browser gives up. It sits well
// *: above the longest caller-side timeout (`PARCEL_LOOKUP_TIMEOUT_MS`, 15s) so
// *: a flow that runs its own AbortController deadline still trips that one
// *: first and keeps its own error message.
export const REQUEST_TIMEOUT_MS = 30000;

const palmettoBaseURL = import.meta.env.VITE_PALMETTO_ENDPOINT;
const dmaBaseURL = import.meta.env.VITE_DAMAGEASSESSMENT_API_ENDPOINT;

// *: Copy of `source` with each named key's value replaced by a placeholder.
// *: Matching is case-insensitive because axios normalizes header casing, so
// *: `Authorization` and `authorization` both have to be caught.
function redactKeys(source: unknown, keys: string[]): unknown {
  if (!source || typeof source !== 'object') return source;
  const targets = keys.map((key) => key.toLowerCase());
  return Object.fromEntries(
    Object.entries(source as Record<string, unknown>).map(([key, value]) => [
      key,
      targets.includes(key.toLowerCase()) ? redactedValue : value,
    ])
  );
}

// *: A log-safe view of a failed request. The Cognito ID token must never reach
// *: the console, so both places a credential can sit -- the `Authorization`
// *: header and a legacy `access_token` param -- are stripped, while message,
// *: status, url and response body stay readable so the failure is diagnosable.
function redactCredentials(error: unknown) {
  const { message, code, config, response } = (error ?? {}) as Partial<AxiosError>;
  return {
    message,
    code,
    status: response?.status,
    data: response?.data,
    method: config?.method,
    url: config?.url,
    params: redactKeys(config?.params, ['access_token']),
    headers: redactKeys(config?.headers, ['authorization']),
  };
}

async function apiFetch<T>(
  api: EndPointTypes,
  controller?: AbortController
): Promise<{ status: number; data: T }> {
  const params: any = api.params || {}; // eslint-disable-line
  const { headers } = apiConfig;
  const baseURL =
    api.defaultBaseURL === BaseURLConfig.DMA
      ? dmaBaseURL
      : api.defaultBaseURL === BaseURLConfig.NULL
        ? null
        : palmettoBaseURL;
  const apiConfigMerge: AxiosRequestConfig = merge.all([
    apiConfig,
    api,
    {
      baseURL,
      headers,
      params,
      timeout: REQUEST_TIMEOUT_MS,
    },
  ]);
  const axiosCreate = axios.create(apiConfigMerge);
  axiosCreate.interceptors.request.use(
    async function (config) {
      if (controller) {
        config.signal = controller.signal;
      }
      // *: The DMA backend's Cognito authorizer reads the ID token raw off the
      // *: `Authorization` header -- no `Bearer` prefix. Scoping the credential
      // *: to DMA explicitly keeps a future non-DMA endpoint from inheriting it
      // *: just by declaring `REQUIRED`.
      // *: happy: jwt present -> raw Authorization header below.
      // *: nil/empty: warned below; the request goes out unauthenticated so the
      // *:   401 is traceable to the empty credential instead of guessed at.
      // *: non-DMA or NOT_REQUIRED: this guard skips the block entirely.
      // *: upstream error: the rejection handler below logs a redacted error.
      if (api.authorization === AuthProtect.REQUIRED && api.defaultBaseURL === BaseURLConfig.DMA) {
        const jwtToken = store.getState().authToken.jwtToken;
        if (jwtToken) {
          config.headers.Authorization = jwtToken;
        } else {
          console.warn(missingJwtWarning);
        }
      }
      return config;
    },
    function (error) {
      console.log('🚀apiFetch function ERROR: ', redactCredentials(error));
      throw Error(error);
    }
  );
  return axiosCreate(api).then(
    (res: AxiosResponse<T>) => {
      return { status: res.status, data: res.data };
    },
    (error) => {
      console.log('🚀apiFetch ERROR: ', redactCredentials(error));
      if (error.response) {
        const apiError = Error(JSON.stringify(error.response.data));
        // *: Carry the HTTP status alongside the message so callers can tell "not
        // *: found" from "request failed" -- the message alone collapses every
        // *: non-2xx into one string. Purely additive; existing callers ignore it.
        (apiError as Error & { status?: number }).status = error.response.status;
        throw apiError;
        // return { status: error.response.status, data: error.response.data };
      } else {
        throw Error(genericErrorMessage);
        // return { status: 500, data: genericErrorMessage };
      }
    }
  );
}

export default apiFetch;
